import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  ReceptionCorrectionsInput,
  ReceptionDto,
  ReceptionReferenceOption,
} from '@anessa/shared-models';
import { WeighbridgeEventsApiBase } from '../../procurement/weighbridge-events-api.base';
import { ReferenceOptions } from '../../procurement/exchange/weighbridge-exchange-api.service';

@Injectable({ providedIn: 'root' })
export class ReceptionsApiService extends WeighbridgeEventsApiBase {
  protected readonly resource = 'receptions';

  /**
   * The plant's Precia reference options for « Corriger… », with the
   * correction's own access (ADEV-1207): the same codes the weighbridge is given.
   */
  getReferenceOptions(plantId: string): Observable<ReferenceOptions> {
    return this.http.get<ReferenceOptions>(`/api/${this.resource}/reference-options`, {
      params: { plantId },
    });
  }

  /** Persist the editable storage destination ("Destination de stockage"). */
  setStorageDestination(
    plantId: string,
    id: string,
    storageDestination: string,
  ): Observable<ReceptionDto> {
    return this.http.patch<ReceptionDto>(
      `/api/${this.resource}/${id}/storage-destination?plantId=${encodeURIComponent(plantId)}`,
      { storageDestination },
    );
  }

  /** Persist the measured dry matter ("MS mesurée"). `null` clears it. */
  setMeasuredDm(plantId: string, id: string, measuredDm: number | null): Observable<ReceptionDto> {
    return this.http.patch<ReceptionDto>(
      `/api/${this.resource}/${id}/measured-dm?plantId=${encodeURIComponent(plantId)}`,
      { measuredDm },
    );
  }

  /** Apply whichever reference-data fields the operator corrected (Producer/Product/Order/Transporter/Container), in one request. Site-Manager-only (EDIT_CORRECT_DATA). */
  applyCorrections(
    plantId: string,
    id: string,
    corrections: ReceptionCorrectionsInput,
  ): Observable<ReceptionDto> {
    return this.http.patch<ReceptionDto>(
      `/api/${this.resource}/${id}/corrections?plantId=${encodeURIComponent(plantId)}`,
      corrections,
    );
  }

  /** Active Wintrans producers, for the Producer correction search-select. */
  getProducers(): Observable<ReceptionReferenceOption[]> {
    return this.http.get<ReceptionReferenceOption[]>(`/api/${this.resource}/producers`);
  }

  /** Active Wintrans products, for the Product correction search-select. */
  getProducts(): Observable<ReceptionReferenceOption[]> {
    return this.http.get<ReceptionReferenceOption[]>(`/api/${this.resource}/products`);
  }

  /** Active Wintrans transporters, for the Transporter correction search-select. */
  getTransporters(): Observable<ReceptionReferenceOption[]> {
    return this.http.get<ReceptionReferenceOption[]>(`/api/${this.resource}/transporters-reference`);
  }

  /** Active Ecovalim containers, for the Container correction search-select (Cases 4/7). Global, not plant-scoped. */
  getContainers(): Observable<ReceptionReferenceOption[]> {
    return this.http.get<ReceptionReferenceOption[]>(`/api/${this.resource}/containers`);
  }

  /** Active Wintrans orders for the plant, for the Order No. correction autocomplete. Empty for Regener/Ecovalim. */
  getOrders(plantId: string): Observable<ReceptionReferenceOption[]> {
    return this.http.get<ReceptionReferenceOption[]>(`/api/${this.resource}/orders`, {
      params: { plantId },
    });
  }

  /** Count of receptions still missing an on-site destination (sidebar dot). */
  getMissingDestinationCount(plantId: string): Observable<number> {
    return this.http
      .get<{ count: number }>(`/api/${this.resource}/missing-destination-count`, {
        params: { plantId },
      })
      .pipe(map((r) => r.count));
  }
}

